import { Checkbox, Heading, Link } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { QuestionCircleIcon } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { RequiredPlanBadge } from '../../../../../../common/components/ProBadge';
import { colors } from '../../../../beta/styles/colors';
import { CustomerChatConditionsEditor } from '../CustomerChatConditionsEditor/CustomerChatConditionsEditor';
import { CustomerChatEditorAccordionItem } from '../CustomerChatEditorAccordionItem/CustomerChatEditorAccordionItem';
import { CustomerChatGreetingBehaviourEditor } from '../CustomerChatGreetingBehaviourEditor/CustomerChatGreetingBehaviourEditor';
import './CustomerChatAdvancedSettings.pcss';

type Props = {
  promoter: PromoterModel;
  usedInSlideout?: boolean;
};
export const CustomerChatAdvancedSettings = (props: Props) => {
  const { promoter, usedInSlideout } = props;
  const { enableDataApi } = promoter;

  const { id: botId } = useSelector(activeChatbotSelector);

  const { updateSetupStepsRemaining } = useChatbotCRUDActions();
  const { update: updatePromoter } = usePromoterCRUDActions();

  const canUseCustomerChatAdvancedSettings = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatAdvancedSettings;
  const modalActions = useModalActions();

  const updatePromoterEnableDataApi = (enableDataApi: boolean) => {
    if (promoter?.id) {
      updateSetupStepsRemaining('chatWidgetUpdate', 'completed');

      updatePromoter(botId, {
        id: promoter.id,
        type: promoter.type,
        enableDataApi,
      } as any);
    }
  };

  const onToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canUseCustomerChatAdvancedSettings) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['CUSTOMER-CHAT-WIDGET__DATA-API'],
        feature: 'customerChatAdvancedSettings',
      });
      return;
    }
    updatePromoterEnableDataApi(e.target.checked);
  };

  return (
    <>
      <CustomerChatGreetingBehaviourEditor
        promoter={promoter}
        usedInSlideout={usedInSlideout}
      />
      <CustomerChatConditionsEditor
        promoter={promoter}
        usedInSlideout={usedInSlideout}
      />

      <CustomerChatEditorAccordionItem
        title={
          <div
            className={
              usedInSlideout
                ? 'customer-chat-advanced-settings__heading--slideout'
                : 'customer-chat-advanced-settings__heading'
            }
          >
            <Heading
              size={usedInSlideout ? 5 : 4}
              text="Custom Data"
              isBold={!usedInSlideout}
            />
            <RequiredPlanBadge billingType={PageBillingType.ProUnicorn} />
          </div>
        }
        descriptionText="Pass custom data into your website chat"
        usedInSlideout={usedInSlideout}
      >
        <div className="customer-chat-advanced-settings__custom-data-container">
          <Checkbox
            isChecked={!!enableDataApi}
            onToggle={onToggle}
            label="Pass custom data into chat widget"
            labelHelpTextClassName={
              usedInSlideout
                ? ' customer-chat-advanced-settings__custom-data-slideout-label'
                : ''
            }
          />
          <Link
            href="https://customers.ai/help/article/dwv1mvqdey-sending-custom-data-from-your-website-into-your-chat-widget"
            isBlank
          >
            <QuestionCircleIcon
              color={usedInSlideout ? '#262626' : colors.accentBlue}
              className="customer-chat-advanced-settings__enable-data-api-help-icon"
            />
          </Link>
        </div>
      </CustomerChatEditorAccordionItem>
    </>
  );
};
